import {data} from '@/embeddings/data';
import {Filter} from './filter';
import {heap} from '@/embeddings/heap';

export class Word {
  constructor(
    readonly label: string,
    readonly index: number,
    readonly weight: number = 1
  ) {}
}

export class Url {
  readonly $_text: string;
  readonly $_ext: string;
  readonly $_link: string;
  public $_filterScores: number[] = [];

  constructor(readonly $_words: Word[], readonly $_index: number, readonly $_extIndex: number, readonly $_price: number, readonly $_linkId: string, readonly $_age: number, readonly $_expired: boolean) {
    const mapStr: any = {0: ".com", 1: ".co", 2: ".io", 3: ".ai"};
    this.$_ext = mapStr[$_extIndex];
    this.$_text = this.$_words.map(word => word.label).join(' ');
    const url = this.$_words.map(word => word.label).join('');
    const ext2 = `${mapStr[$_extIndex]}`.replace('.', '');
    this.$_link = `https://www.godaddy.com/domain-auctions/${url}-${ext2}-${$_linkId}`;
  }

  length(filter: Filter): number {
    let total = 0;
    for (let i = 0; i < this.$_words.length; i++) {
      if (i == 0 && filter.$_firstWord) {
        total++;
      } else if (i == this.$_words.length - 1 && filter.$_lastWord) {
        total++;
      } else if (i > 0 && i < this.$_words.length - 1 && filter.$_middleWords) {
        total++;
      }
    }
    return total;
  }

  iterate(filter: Filter, fn: (word: Word, idx: number) => void) {
    for (let i = 0; i < this.$_words.length; i++) {
      if (i == 0 && filter.$_firstWord) {
        fn(this.$_words[i], i);
      } else if (i == this.$_words.length - 1 && filter.$_lastWord) {
        fn(this.$_words[i], i);
      } else if (i > 0 && i < this.$_words.length - 1 && filter.$_middleWords) {
        fn(this.$_words[i], i);
      }
    }
  }

  get score(): number {
    let totalScore = 0;
    let maxScore = 0;
    for (let i = 0; i < this.$_filterScores.length; i++) {
      totalScore += this.$_filterScores[i];
      maxScore++;
    }
    return totalScore / maxScore;
  }
}

export interface UrlResult {
  $_url: Url;
  $_urlKey: number;
  $_filters: {
    $_filter: Filter;
    $_score: number | null;
  }[];
}

export interface UrlResults {
  $_results: UrlResult[];
  $_minScore: number;
  $_maxScore: number;
}

export class Controller {
  // Map the word as a string to its index.
  public $_wordMap: {[key: string]: number};

  public $_filters: Filter[] = [];

  // Unique key for filtered URLs.
  $_urlKey = 1;

  /**
   * @param words A list of all the words possible in frequency order.
   * @param urls A list of all the URLs.
   * @param dataset The embedding dataset.
   */
  constructor(
    readonly $_words: string[],
    readonly $_urls: Url[],
    readonly $_dataset: data.DataSet
  ) {
    // Populate the word mapping.
    const wordMap: {[key: string]: number} = {};
    for (let i = 0; i < $_words.length; i++) {
      wordMap[$_words[i]] = i;
    }
    this.$_wordMap = wordMap;
  }

  $_invalidate = true;

  filteredUrls(filters: Filter[], limit = 200): UrlResults {
    const h = new heap.KMin<Url>(limit);

    let minScore: number | null = null;
    let maxScore: number | null = null;

    for (let i = 0; i < this.$_urls.length; i++) {
      const url = this.$_urls[i];

      let totalScore = 0;
      let highestPossible = 0;
      for (let j = 0; j < filters.length; j++) {
        const filter = filters[j];
        let score = filter.score(url, this);
        if (score == null || isNaN(score)) continue;
        if (score < 0) {
          score =
            -Math.pow(Math.abs(score), filter.$_power) *
            filter.$_scale *
            (filter.$_inv ? -1 : 1);
        } else {
          score =
            Math.pow(Math.abs(score), filter.$_power) *
            filter.$_scale *
            (filter.$_inv ? -1 : 1);
        }
        if (minScore == null || score < minScore) {
          minScore = score;
        }
        if (maxScore == null || score > maxScore) {
          maxScore = score;
        }
        totalScore += score;
        highestPossible += Math.abs(filter.$_scale);
      }

      if (highestPossible != 0) {
        h.add(-totalScore / highestPossible, url);
      }
    }

    const results = h.getMinKItems().map(url => {
      const outputs = filters.map(filter => {
        let score = filter.score(url, this);

        if (score != null && isNaN(score)) score = null;
        if (score != null) {
          if (score < 0) {
            score =
              -Math.pow(Math.abs(score), filter.$_power) *
              filter.$_scale *
              (filter.$_inv ? -1 : 1);
          } else {
            score =
              Math.pow(Math.abs(score), filter.$_power) *
              filter.$_scale *
              (filter.$_inv ? -1 : 1);
          }
        }

        return {
          $_filter: filter,
          $_score: score,
        };
      });

      return {
        $_url: url,
        $_filters: outputs,
        $_urlKey: this.$_urlKey++,
      };
    });

    if (this.$_invalidate) {
      this.$_invalidate = false;
    }
    for (let i = 0; i < filters.length; i++) {
      filters[i].$_invalidate = false;
    }

    return {
      $_results: results,
      $_minScore: minScore!,
      $_maxScore: maxScore!,
    };
  }
}
